import React, { useState } from 'react';
import { MessageCircle, X, Send, CheckCircle, Loader2, PhoneCall, HelpCircle } from 'lucide-react';
import chatFaq from '../../chat-faq.json';

interface FormData {
  fullName: string;
  email: string;
  company: string;
  service: string;
  purpose: string;
  additionalDetails: string;
}

interface WebhookResponse {
  success: boolean;
  message: string;
  recordId?: string;
}

interface ChatWidgetProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ isOpen, setIsOpen }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [showForm, setShowForm] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{type: 'user' | 'bot', content: string}>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<string>>(new Set());
  const chatMessagesRef = React.useRef<HTMLDivElement>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    company: '',
    service: 'Executive AI Advisory & Solutions',
    purpose: 'consultation',
    additionalDetails: ''
  });

  // Generate session ID when component mounts
  React.useEffect(() => {
    if (!currentSessionId) {
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setCurrentSessionId(sessionId);
    }
  }, [currentSessionId]);

  // Function to search FAQ and find matching answer
  const searchFAQ = (question: string): string | null => {
    const normalizedQuestion = question.toLowerCase().trim();
    
    // Check if we've already answered this question (or very similar)
    const questionKey = normalizedQuestion.replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
    if (answeredQuestions.has(questionKey)) {
      return null; // Return null to trigger form opening
    }
    
    // Handle greetings
    const greetings = ['hi', 'hello', 'hey', 'good morning', 'good afternoon', 'good evening', 'greetings'];
    if (greetings.some(greeting => normalizedQuestion.includes(greeting))) {
      setAnsweredQuestions(prev => new Set(prev).add(questionKey));
      return [
        "Hello, and welcome to DigiMData.",
        "",
        "We help with:",
        "1) Executive AI Advisory & Solutions — strategic AI guidance and governance that turns business objectives into production-ready AI programs.",
        "2) AI Execution & Engineering — embedded teams that design, govern, and deploy AI systems (LLM, RAG, automation, ERP/CRM integration).",
        "",
        "Our approach: Discovery → Design → Pilot → Production.",
        "",
        "Ask a question here or open the form to book a strategy call."
      ].join("\n");
    }
    
    // Handle services inquiry - summary of our core services
    if (normalizedQuestion.includes('services') || normalizedQuestion.includes('what services')) {
      setAnsweredQuestions(prev => new Set(prev).add(questionKey));
      return [
        "Here’s how we engage:",
        "",
        "1) Executive AI Advisory & Solutions",
        "   Strategic AI guidance and governance that turns business objectives into actionable, production-ready AI programs. Includes AI Strategy & Governance, Executive & Board Visibility, and a Dedicated Advisory Model (on-demand leadership, embedded teams, project-based or retainer).",
        "",
        "2) AI Execution & Engineering",
        "   Embedded AI execution that turns strategy into deployed systems: LLM integration & RAG, custom copilots, ERP/CRM integration, secure data pipelines, and dedicated full-stack AI engineers.",
        "",
        "We follow Discovery → Design → Pilot → Production.",
        "",
        "Tell me which is most relevant, or open the form to book a strategy call."
      ].join("\n");
    }
    
    // Handle specific service keywords for core offerings
    if (normalizedQuestion.includes('executive ai advisory') || normalizedQuestion.includes('executive ai advisory & solutions')) {
      setAnsweredQuestions(prev => new Set(prev).add(questionKey));
      return [
        "Executive AI Advisory & Solutions:",
        "",
        "Strategic AI guidance and governance that turns business objectives into actionable, production-ready AI programs.",
        "",
        "• AI Strategy & Governance — roadmaps, use case prioritization, governance & compliance, vendor oversight.",
        "• Executive & Board Visibility — dashboards, KPIs, reporting, board-level advisory.",
        "• Dedicated Advisory Model — on-demand executive AI leadership, embedded teams, project-based or retainer engagement."
      ].join("\n");
    }

    if (normalizedQuestion.includes('roadmap') || normalizedQuestion.includes('strategy') || normalizedQuestion.includes('planning') || normalizedQuestion.includes('discovery') || normalizedQuestion.includes('pilot') || normalizedQuestion.includes('production')) {
      setAnsweredQuestions(prev => new Set(prev).add(questionKey));
      return [
        "Our approach: Discovery → Design → Pilot → Production",
        "",
        "• Discovery — Identify high-impact AI opportunities aligned to business goals.",
        "• Design — Build AI roadmaps, governance frameworks, and execution plans.",
        "• Pilot — Validate models and solutions in controlled, measurable environments.",
        "• Production — Scale and deploy AI systems with clear ownership and ROI.",
        "",
        "This is how we deliver both Executive AI Advisory & Solutions and AI Execution & Engineering."
      ].join("\n");
    }

    if (normalizedQuestion.includes('execution') || normalizedQuestion.includes('engineering') || (normalizedQuestion.includes('team') && (normalizedQuestion.includes('ai') || normalizedQuestion.includes('dedicated')))) {
      setAnsweredQuestions(prev => new Set(prev).add(questionKey));
      return [
        "AI Execution & Engineering:",
        "",
        "Embedded AI execution that turns strategy into deployed systems.",
        "",
        "• AI Application Development — LLM integration & RAG systems, custom AI copilots, domain-specific automation.",
        "• Enterprise Integration — ERP/CRM integration, secure data pipelines, workflow automation.",
        "• Dedicated Delivery Model — Embedded full-stack AI engineers, project-based or retainer engagement."
      ].join("\n");
    }

    // Try to match against editable chat FAQ entries
    const faqs: Array<{
      id: string;
      question: string;
      answer: string;
      keywords?: string[];
    }> = chatFaq as any;

    for (const faq of faqs) {
      const keywords = faq.keywords || [];
      if (keywords.some((kw) => normalizedQuestion.includes(kw.toLowerCase()))) {
        setAnsweredQuestions((prev) => new Set(prev).add(questionKey));
        return faq.answer;
      }
    }

    // If no match found, return fallback message
    return "I may not have that specific answer in the quick FAQ. You can rephrase it, or open the form so we can respond with more context.";
  };

  // Scroll to bottom of chat messages
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [chatMessages, isSearching]);

  const handleSendMessage = async () => {
    if (!userMessage.trim()) return;
    
    const newUserMessage = userMessage.trim();
    setChatMessages(prev => [...prev, { type: 'user', content: newUserMessage }]);
    setUserMessage('');
    setIsSearching(true);
    
    // Simulate a brief delay for better UX
    setTimeout(() => {
      const answer = searchFAQ(newUserMessage);
      if (answer === null) {
        // Question already answered, open form
        setChatMessages(prev => [...prev, { type: 'bot', content: "I see you've asked a similar question before. Let me connect you with our team for personalized assistance!" }]);
        setTimeout(() => {
          setShowForm(true);
        }, 1000);
      } else {
        setChatMessages(prev => [...prev, { type: 'bot', content: answer }]);
      }
      setIsSearching(false);
    }, 500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const services = [
    { value: 'Executive AI Advisory & Solutions', label: 'Executive AI Advisory & Solutions' },
    { value: 'AI Execution & Engineering', label: 'AI Execution & Engineering' }
  ];

  const serviceDescriptions: Record<string, string> = {
    'Executive AI Advisory & Solutions':
      'Strategic AI guidance and governance that turns business objectives into actionable, production-ready AI programs. Includes AI Strategy & Governance, Executive & Board Visibility, and Dedicated Advisory Model (on-demand leadership, embedded teams, project-based or retainer).',
    'AI Execution & Engineering':
      'Embedded AI execution that turns strategy into deployed systems: LLM integration & RAG, custom copilots, ERP/CRM integration, secure data pipelines, and dedicated full-stack AI engineers.'
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company/Organization is required';
    }

    if (!formData.purpose) {
      newErrors.purpose = 'Please select a purpose';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Real-time email validation
    if (field === 'email' && value && !validateEmail(value)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        company: formData.company,
        service: formData.service,
        purpose: formData.purpose,
        additionalDetails: formData.additionalDetails,
        sessionId: currentSessionId,
        source: 'website-chat-widget',
      };

      const response = await fetch('/.netlify/functions/submit-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result: WebhookResponse = await response.json();

      if (result.success) {
        setResponseMessage(result.message);
        setIsSuccess(true);
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error: any) {
      console.error('Submission error:', error);
      const message =
        (error && error.message) ||
        'There was an error submitting your request. Please try again in a moment.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      company: '',
      service: 'Executive AI Advisory & Solutions',
      purpose: 'consultation',
      additionalDetails: ''
    });
    setErrors({});
    setIsSuccess(false);
    setCurrentSessionId('');
    setResponseMessage('');
    setShowForm(false);
    setChatMessages([]);
    setUserMessage('');
    setAnsweredQuestions(new Set());
  };

  const handleClose = () => {
    setIsOpen(false);
    if (isSuccess) {
      setTimeout(resetForm, 300); // Reset after close animation
    }
  };

  // Close widget on ESC key
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setIsOpen]);

  const floatingButtonStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 9999,
  };

  const bubbleButtonStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #C9935F 0%, #A3733F 100%)',
    color: '#ffffff',
    borderRadius: '9999px',
    width: '56px',
    height: '56px',
    border: 'none',
    display: isOpen ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 10px 25px rgba(140, 88, 32, 0.45)',
  };

  const widgetContainerStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 9999,
    width: '360px',
    maxWidth: 'calc(100vw - 24px)',
    fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  const widgetCardStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 18px 45px rgba(15, 23, 42, 0.25)',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #C9935F 0%, #A3733F 100%)',
    padding: '14px 16px',
    color: '#ffffff',
  };

  const headerTitleStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    margin: 0,
  };

  const headerSubtitleStyle: React.CSSProperties = {
    fontSize: '12px',
    opacity: 0.9,
    margin: '2px 0 0 0',
  };

  const headerTopRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const iconButtonStyle: React.CSSProperties = {
    border: 'none',
    background: 'transparent',
    color: '#bfdbfe',
    cursor: 'pointer',
    padding: '4px',
  };

  const bodyStyle: React.CSSProperties = {
    padding: '20px',
    maxHeight: '70vh',
    overflowY: 'auto',
    backgroundColor: '#f9fafb',
  };

  return (
    <>
      {/* Chat Button */}
      <div style={floatingButtonStyle}>
        <button
          onClick={() => setIsOpen(true)}
          style={bubbleButtonStyle}
          aria-label="Open chat"
        >
          <MessageCircle size={24} />
        </button>
      </div>

      {/* Chat Widget */}
      {isOpen && (
        <div style={widgetContainerStyle}>
          <div style={widgetCardStyle}>
            {/* Header */}
            <div style={headerStyle}>
              <div style={headerTopRowStyle}>
                <div>
                  <h3 style={headerTitleStyle}>
                    {showForm ? 'Contact Form' : 'Quick Chat'}
                  </h3>
                  <p style={headerSubtitleStyle}>
                    {showForm
                      ? 'Tell us briefly what you’d like to discuss.'
                      : 'Ask a question, or switch to the form to book a call.'}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  style={iconButtonStyle}
                  aria-label="Close chat"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={bodyStyle}>
              {isSuccess ? (
                // Success State
                <div
                  style={{
                    textAlign: 'center',
                    padding: '24px 8px',
                  }}
                >
                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      margin: '0 auto 16px',
                      borderRadius: '9999px',
                      backgroundColor: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircle size={32} color="#16a34a" />
                  </div>
                  <h4
                    style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#111827',
                      margin: '0 0 8px',
                    }}
                  >
                    Thank you!
                  </h4>
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#4b5563',
                      margin: '0 0 16px',
                      lineHeight: 1.5,
                    }}
                  >
                    {responseMessage}
                  </p>
                  <button
                    onClick={resetForm}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#C9935F',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    Submit another request
                  </button>
                </div>
              ) : !showForm ? (
                // Chat Interface
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Welcome Message */}
                  {chatMessages.length === 0 && (
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #FFF7EE 0%, #FDE3C5 100%)',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        border: '1px solid #F3C89A',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                      }}
                    >
                      <div
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '9999px',
                          background:
                            'linear-gradient(135deg, #C9935F 0%, #A3733F 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ffffff',
                          flexShrink: 0,
                        }}
                      >
                        <MessageCircle size={16} />
                      </div>
                      <p
                        style={{
                          fontSize: '13px',
                          color: '#7A4E23',
                          margin: 0,
                          lineHeight: 1.5,
                        }}
                      >
                        This is DigiMData Quick Chat. Ask about Executive AI Advisory & Solutions (strategy, governance, board visibility, on-demand advisory) or AI Execution & Engineering (LLM, RAG, integration, dedicated teams). Or open the form to book a strategy call.
                      </p>
                    </div>
                  )}

                  {/* Chat Messages */}
                  <div
                    ref={chatMessagesRef}
                    style={{
                      maxHeight: '240px',
                      overflowY: 'auto',
                      paddingRight: '4px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    {chatMessages.map((message, index) => {
                      const isUser = message.type === 'user';
                      return (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: isUser ? 'flex-end' : 'flex-start',
                          }}
                        >
                          <div
                            style={{
                              maxWidth: '80%',
                              padding: '8px 10px',
                              borderRadius: '12px',
                              fontSize: '13px',
                              lineHeight: 1.5,
                              backgroundColor: isUser ? '#C9935F' : '#f3f4f6',
                              color: isUser ? '#ffffff' : '#111827',
                            }}
                          >
                            {message.content.split('\n').map((line, lineIndex, arr) => (
                              <span key={lineIndex}>
                                {line}
                                {lineIndex < arr.length - 1 && <br />}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {/* Loading indicator */}
                    {isSearching && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: '#f3f4f6',
                            padding: '8px 10px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '12px',
                            color: '#4b5563',
                          }}
                        >
                          <Loader2 size={14} className="animate-spin" />
                          <span>Thinking…</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Message Input + Form Switch */}
                  <div
                    style={{
                      borderTop: '1px solid #e5e7eb',
                      paddingTop: '12px',
                      marginTop: '4px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        marginBottom: '8px',
                      }}
                    >
                      <input
                        type="text"
                        value={userMessage}
                        onChange={(e) => setUserMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask about Executive AI Advisory & Solutions or AI Execution & Engineering..."
                        style={{
                          flex: 1,
                          padding: '8px 10px',
                          fontSize: '14px',
                          borderRadius: '10px',
                          border: '1px solid #d1d5db',
                          outline: 'none',
                        }}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!userMessage.trim() || isSearching}
                        style={{
                          padding: '8px 10px',
                          borderRadius: '10px',
                          border: 'none',
                          background: userMessage.trim() && !isSearching
                            ? 'linear-gradient(135deg, #C9935F 0%, #A3733F 100%)'
                            : '#9ca3af',
                          color: '#ffffff',
                          cursor:
                            userMessage.trim() && !isSearching ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Send size={16} />
                      </button>
                    </div>

                    <button
                      onClick={() => setShowForm(true)}
                      style={{
                        width: '100%',
                        marginTop: '4px',
                        padding: '8px 10px',
                        borderRadius: '10px',
                        border: 'none',
                        background:
                          'linear-gradient(135deg, rgba(201,147,95,0.16) 0%, rgba(163,115,63,0.16) 100%)',
                        color: '#A3733F',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Prefer a structured conversation?{' '}
                      <span style={{ fontWeight: 700 }}>Open the short form</span> →
                    </button>
                  </div>
                </div>
              ) : (
                // Form
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Back to Chat Button */}
                  <button
                    onClick={() => setShowForm(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#C9935F',
                      fontSize: '13px',
                      fontWeight: 500,
                      textAlign: 'left',
                      cursor: 'pointer',
                      marginBottom: '4px',
                    }}
                  >
                    ← Back to Quick Chat
                  </button>

                  <form
                    onSubmit={handleSubmit}
                    style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
                  >
                    {submitError && (
                      <div
                        style={{
                          padding: '8px 10px',
                          borderRadius: '10px',
                          backgroundColor: '#FEF2F2',
                          border: '1px solid #FCA5A5',
                          color: '#B91C1C',
                          fontSize: '12px',
                          lineHeight: 1.5,
                        }}
                      >
                        {submitError}
                      </div>
                    )}
                    {/* Full Name */}
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '4px',
                        }}
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                        placeholder="Enter your full name"
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          fontSize: '14px',
                          borderRadius: '10px',
                          border: `1px solid ${
                            errors.fullName ? '#dc2626' : '#d1d5db'
                          }`,
                          outline: 'none',
                        }}
                      />
                      {errors.fullName && (
                        <p
                          style={{
                            color: '#dc2626',
                            fontSize: '11px',
                            marginTop: '2px',
                          }}
                        >
                          {errors.fullName}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '4px',
                        }}
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter your email address"
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          fontSize: '14px',
                          borderRadius: '10px',
                          border: `1px solid ${
                            errors.email ? '#dc2626' : '#d1d5db'
                          }`,
                          outline: 'none',
                        }}
                      />
                      {errors.email && (
                        <p
                          style={{
                            color: '#dc2626',
                            fontSize: '11px',
                            marginTop: '2px',
                          }}
                        >
                          {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Company */}
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '4px',
                        }}
                      >
                        Company/Organization *
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        placeholder="Enter your company name"
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          fontSize: '14px',
                          borderRadius: '10px',
                          border: `1px solid ${
                            errors.company ? '#dc2626' : '#d1d5db'
                          }`,
                          outline: 'none',
                        }}
                      />
                      {errors.company && (
                        <p
                          style={{
                            color: '#dc2626',
                            fontSize: '11px',
                            marginTop: '2px',
                          }}
                        >
                          {errors.company}
                        </p>
                      )}
                    </div>

                    {/* Service Selection */}
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '4px',
                        }}
                      >
                        Primary service of interest *
                      </label>
                      <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>
                        Executive AI Advisory & Solutions or AI Execution & Engineering
                      </p>
                      <select
                        value={formData.service}
                        onChange={(e) => handleInputChange('service', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          fontSize: '14px',
                          borderRadius: '10px',
                          border: `1px solid ${
                            errors.service ? '#dc2626' : '#d1d5db'
                          }`,
                          outline: 'none',
                          backgroundColor: '#ffffff',
                        }}
                      >
                        {services.map((service) => (
                          <option key={service.value} value={service.value}>
                            {service.label}
                          </option>
                        ))}
                      </select>
                      {errors.service && (
                        <p
                          style={{
                            color: '#dc2626',
                            fontSize: '11px',
                            marginTop: '2px',
                          }}
                        >
                          {errors.service}
                        </p>
                      )}

                      {/* Service Description */}
                      {formData.service && serviceDescriptions[formData.service] && (
                        <div
                          style={{
                            marginTop: '6px',
                            padding: '8px 10px',
                            borderRadius: '10px',
                            background:
                              'linear-gradient(135deg, #FFF7EE 0%, #FDE3C5 100%)',
                            border: '1px solid #F3C89A',
                          }}
                        >
                          <p
                            style={{
                              fontSize: '12px',
                              color: '#7A4E23',
                              margin: 0,
                              lineHeight: 1.5,
                            }}
                          >
                            {serviceDescriptions[formData.service]}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Purpose */}
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '4px',
                        }}
                      >
                        Purpose *
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '13px',
                            color: '#374151',
                            gap: '6px',
                          }}
                        >
                          <input
                            type="radio"
                            name="purpose"
                            value="consultation"
                            checked={formData.purpose === 'consultation'}
                            onChange={(e) =>
                              handleInputChange('purpose', e.target.value)
                            }
                            style={{ marginRight: '2px' }}
                          />
                          <PhoneCall size={16} color="#C9935F" />
                          <span>Book a short strategy call</span>
                        </label>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '13px',
                            color: '#374151',
                            gap: '6px',
                          }}
                        >
                          <input
                            type="radio"
                            name="purpose"
                            value="questions"
                            checked={formData.purpose === 'questions'}
                            onChange={(e) =>
                              handleInputChange('purpose', e.target.value)
                            }
                            style={{ marginRight: '2px' }}
                          />
                          <HelpCircle size={16} color="#C9935F" />
                          <span>Ask specific questions via email</span>
                        </label>
                      </div>
                      {errors.purpose && (
                        <p
                          style={{
                            color: '#dc2626',
                            fontSize: '11px',
                            marginTop: '2px',
                          }}
                        >
                          {errors.purpose}
                        </p>
                      )}
                    </div>

                    {/* Additional Details */}
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '4px',
                        }}
                      >
                        Additional Details
                      </label>
                      <textarea
                        value={formData.additionalDetails}
                        onChange={(e) =>
                          handleInputChange('additionalDetails', e.target.value)
                        }
                        rows={3}
                        placeholder="Share a bit about your context, timelines, and goals..."
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          fontSize: '14px',
                          borderRadius: '10px',
                          border: `1px solid ${
                            errors.additionalDetails ? '#dc2626' : '#d1d5db'
                          }`,
                          outline: 'none',
                          resize: 'none',
                        }}
                      />
                      {errors.additionalDetails && (
                        <p
                          style={{
                            color: '#dc2626',
                            fontSize: '11px',
                            marginTop: '2px',
                          }}
                        >
                          {errors.additionalDetails}
                        </p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        width: '100%',
                        marginTop: '4px',
                        padding: '10px 12px',
                        borderRadius: '9999px',
                        border: 'none',
                        background: isSubmitting
                          ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                          : 'linear-gradient(135deg, #0176d3 0%, #014486 100%)',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: isSubmitting ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          <span>Submitting…</span>
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          <span>Submit Request</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;